import React, { useEffect, useState } from 'react';
import { MessageCircle, Phone, Gift, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SiteSettings {
  free_chat_credits: number;
  free_voice_minutes: number;
  enable_free_chat: boolean;
  enable_free_voice: boolean;
}

interface PromotionalCredit {
  id: string;
  promo_chat_credits: number | null;
  promo_voice_minutes: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export default function DynamicCreditsBanner() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [activePromo, setActivePromo] = useState<PromotionalCredit | null>(null);
  const [countdown, setCountdown] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreditsInfo();

    // Subscribe to real-time changes in site_settings
    const settingsChannel = supabase
      .channel('site_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings'
        },
        () => {
          fetchCreditsInfo();
        }
      )
      .subscribe();

    // Subscribe to real-time changes in promotional_credits
    const promosChannel = supabase
      .channel('promotional_credits_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'promotional_credits'
        },
        () => {
          fetchCreditsInfo();
        }
      )
      .subscribe();

    // Still keep interval as fallback
    const interval = setInterval(() => {
      fetchCreditsInfo();
    }, 60000);

    return () => {
      clearInterval(interval);
      settingsChannel.unsubscribe();
      promosChannel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!activePromo) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(activePromo.end_date).getTime();
      const distance = end - now;

      if (distance < 0) {
        setCountdown('Expired');
        fetchCreditsInfo();
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setCountdown(`${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [activePromo]);

  const fetchCreditsInfo = async () => {
    try {
      const [settingsResult, promosResult] = await Promise.all([
        supabase.from('site_settings').select('*').eq('id', 1).single(),
        supabase
          .from('promotional_credits')
          .select('*')
          .eq('is_active', true)
          .gte('end_date', new Date().toISOString())
          .lte('start_date', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      if (settingsResult.data) {
        setSettings(settingsResult.data);
      }

      if (promosResult.data) {
        setActivePromo(promosResult.data);
      } else {
        setActivePromo(null);
      }
    } catch (err) {
      console.error('Error fetching credits info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !settings) {
    return null;
  }

  const chatCredits = activePromo?.promo_chat_credits ?? (settings.enable_free_chat ? settings.free_chat_credits : 0);
  const voiceMinutes = activePromo?.promo_voice_minutes ?? (settings.enable_free_voice ? settings.free_voice_minutes : 0);
  const hasChat = chatCredits > 0;
  const hasVoice = voiceMinutes > 0;

  if (!hasChat && !hasVoice) {
    return (
      <div className="flex justify-center gap-6 mb-12">
        <div className="bg-white/20 backdrop-blur-sm rounded-full px-8 py-3 border-2 border-white/30">
          <div className="flex items-center gap-2 text-white">
            <Gift className="w-5 h-5" />
            <span className="font-semibold text-lg">Connect with Expert Advisors</span>
          </div>
        </div>
      </div>
    );
  }

  if (activePromo) {
    return (
      <div className="flex justify-center gap-6 mb-12 animate-pulse-subtle">
        <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-full px-8 py-4 border-4 border-white shadow-2xl transform hover:scale-105 transition-transform">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-white">
              <Gift className="w-6 h-6 animate-bounce" />
              <span className="font-bold text-xl">LIMITED TIME OFFER!</span>
            </div>
            <div className="flex items-center gap-4 text-white">
              {hasChat && (
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <span className="font-semibold">{chatCredits} Free Messages</span>
                </div>
              )}
              {hasChat && hasVoice && <span className="text-white/80">+</span>}
              {hasVoice && (
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">{voiceMinutes} Free Minutes</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-white/90 text-sm bg-white/20 px-4 py-1 rounded-full">
              <Clock className="w-4 h-4" />
              <span className="font-semibold">Ends in {countdown}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center gap-6 mb-12">
      {hasChat && (
        <div className="bg-white/20 backdrop-blur-sm rounded-full px-8 py-3 border-2 border-white/30 hover:bg-white/30 transition-colors">
          <div className="flex items-center gap-2 text-white">
            <MessageCircle className="w-5 h-5" />
            <span className="font-semibold">{chatCredits} Free {chatCredits === 1 ? 'Message' : 'Messages'}</span>
          </div>
        </div>
      )}
      {hasVoice && (
        <div className="bg-white/20 backdrop-blur-sm rounded-full px-8 py-3 border-2 border-white/30 hover:bg-white/30 transition-colors">
          <div className="flex items-center gap-2 text-white">
            <Phone className="w-5 h-5" />
            <span className="font-semibold">{voiceMinutes} Free {voiceMinutes === 1 ? 'Minute' : 'Minutes'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
